import { Avatar, AvatarFallback, AvatarGroup, AvatarGroupCount } from "@/components/ui/avatar";
import { fetchSessionUser } from "@/lib/api";
import {
  COLLAB_IA,
  PRESENCE_AUX_LABEL,
  listPresence,
  publishPresence,
  viewersOnSurface,
  type CollabSurface,
  type PresenceSnapshot,
  type PresenceViewer,
} from "@/lib/collaboration";
import { useCallback, useEffect, useState } from "react";

const HEARTBEAT_MS = 12_000;
const EXPIRY_TICK_MS = 1_000;
const STACK_CAP = 5;

function viewerInitials(viewer: PresenceViewer): string {
  const name = (viewer.display_name || viewer.subject || "?").trim();
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0]!.charAt(0)}${parts[1]!.charAt(0)}`.toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

/**
 * PresenceAux V1.1 — avatar stack + dashed label. Never a product hero,
 * never copper-underline, never a Review/Issue status source.
 */
export function PresenceRail({
  orgSlug,
  projectSlug,
  surface,
  objectId,
  compact = false,
}: {
  orgSlug?: string;
  projectSlug?: string;
  surface: CollabSurface;
  objectId?: string | null;
  compact?: boolean;
}) {
  const [snapshot, setSnapshot] = useState<PresenceSnapshot | null>(null);
  const [denied, setDenied] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());

  const beat = useCallback(async () => {
    if (!orgSlug || !projectSlug) return;
    try {
      const user = await fetchSessionUser();
      const next = await publishPresence(orgSlug, projectSlug, {
        surface,
        object_id: objectId ?? null,
        display_name: user?.name ?? user?.email ?? null,
      });
      setSnapshot(next);
      setDenied(false);
      setNowMs(Date.now());
    } catch (err) {
      const message = err instanceof Error ? err.message : "";
      if (message.includes("not a project member") || message.includes("forbidden")) {
        setDenied(true);
        setSnapshot(null);
        return;
      }
      try {
        setSnapshot(await listPresence(orgSlug, projectSlug));
        setNowMs(Date.now());
      } catch {
        /* keep last snapshot; local TTL still drops expired viewers */
      }
    }
  }, [orgSlug, projectSlug, surface, objectId]);

  useEffect(() => {
    void beat();
    const id = window.setInterval(() => void beat(), HEARTBEAT_MS);
    return () => window.clearInterval(id);
  }, [beat]);

  useEffect(() => {
    const id = window.setInterval(() => setNowMs(Date.now()), EXPIRY_TICK_MS);
    return () => window.clearInterval(id);
  }, []);

  const viewers = viewersOnSurface(snapshot, surface, objectId, nowMs);
  const overflow = viewers.length > STACK_CAP ? viewers.length - STACK_CAP : 0;

  return (
    <div
      className={`inline-flex items-center gap-1.5 text-[10px] text-muted-foreground ${compact ? "justify-end" : ""}`}
      data-testid="presence-rail"
      data-mission-id="panel:presence"
      data-presence-aux="v1.1"
      data-surface={surface}
      data-compact={compact ? "1" : "0"}
      title={COLLAB_IA.presenceHint}
      aria-label={COLLAB_IA.presenceHint}
    >
      <span
        data-testid="presence-v11-label"
        className="rounded-sm border border-dashed border-border px-1.5 py-px font-mono text-[10px] tracking-wide text-muted-foreground"
      >
        {PRESENCE_AUX_LABEL}
      </span>
      {denied ? (
        <span data-testid="presence-denied">membership required</span>
      ) : viewers.length === 0 ? (
        <span data-testid="presence-empty">ephemeral</span>
      ) : (
        <AvatarGroup className="-space-x-1.5" data-testid="presence-viewers">
          {viewers.slice(0, STACK_CAP).map((viewer) => (
            <Avatar
              key={viewer.subject}
              size="sm"
              title={viewer.display_name || viewer.subject}
              data-subject={viewer.subject}
            >
              <AvatarFallback>{viewerInitials(viewer)}</AvatarFallback>
            </Avatar>
          ))}
          {overflow > 0 ? <AvatarGroupCount data-testid="presence-overflow">+{overflow}</AvatarGroupCount> : null}
        </AvatarGroup>
      )}
    </div>
  );
}
